import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '@wordpress/i18n';
import CptTable from './CptTable';
import { resourceColumns } from './CptTable/columns/resources';
import { apiFetch, fetchTableRows } from '../scripts/api';
import { downloadCsv } from '../scripts/exportCsv';

const taxNames = ( c ) => c?.nodes?.map( ( n ) => n.name ).join( '; ' ) ?? '';
const postTitles = ( c ) =>
	c?.nodes?.map( ( n ) => n.title ).join( '; ' ) ?? '';

function exportResourcesToCSV( rows ) {
	const headers = [
		__( 'Resource', 'anacrm' ),
		__( 'Link', 'anacrm' ),
		__( 'File', 'anacrm' ),
		__( 'Municipality', 'anacrm' ),
		__( 'Territory', 'anacrm' ),
		__( 'Region', 'anacrm' ),
		__( 'Organisation', 'anacrm' ),
		__( 'Resource type', 'anacrm' ),
		__( 'Use', 'anacrm' ),
		__( 'Duration', 'anacrm' ),
	];
	const dataRows = rows.map( ( r ) => [
		r.title ?? '',
		r.contactWebsite ?? '',
		r.file?.node?.mediaItemUrl ?? '',
		taxNames( r.cities ),
		taxNames( r.territories ),
		taxNames( r.regions ),
		postTitles( r.orgRef ),
		taxNames( r.resourceTypes ),
		taxNames( r.useTerms ),
		taxNames( r.durations ),
	] );
	downloadCsv(
		headers,
		dataRows,
		`resources-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}

export default function ResourceTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	useEffect( () => {
		fetchTableRows( 'resource' )
			.then( ( nodes ) => setData( nodes ) )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/resource/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not delete the resource:', 'anacrm' ) + err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-resource', handler );
		return () =>
			document.removeEventListener( 'anacrm:share-resource', handler );
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() => resourceColumns( { onDelete: handleDelete } ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'workScopes',
			label: __( 'Work scope', 'anacrm' ),
			getTerms: ( row ) =>
				row.workScopes?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'resourceTypes',
			label: __( 'Resource type', 'anacrm' ),
			getTerms: ( row ) =>
				row.resourceTypes?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'useTerms',
			label: __( 'Use', 'anacrm' ),
			getTerms: ( row ) =>
				row.useTerms?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'durations',
			label: __( 'Duration', 'anacrm' ),
			getTerms: ( row ) =>
				row.durations?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'territories',
			label: __( 'Territory', 'anacrm' ),
			getTerms: ( row ) =>
				row.territories?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'orgRef',
			label: __( 'Organisation', 'anacrm' ),
			getTerms: ( row ) =>
				row.orgRef?.nodes?.map( ( n ) => n.title ) ?? [],
		},
		{
			columnId: 'keywords',
			label: __( 'Keywords…', 'anacrm' ),
			type: 'text',
		},
	];

	const shareContainer = document.getElementById( 'anacrm-share-resource' );

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				exportEvent="anacrm:export-resource"
				onExportRows={ exportResourcesToCSV }
				searchPlaceholder={ __( 'Search resources…', 'anacrm' ) }
				fitWidth
			/>
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
